/**
 * Page Transition
 *
 * Provides animated page transitions by intercepting link clicks and triggering
 * Webflow IX animations. Supports namespace-based transitions for different page
 * types (home, contact, etc.) and both MPA and SPA architectures.
 *
 * Features:
 * - Auto link interception for seamless page transitions (MPA mode)
 * - Namespace system for page-specific transitions (Barba.js compatible)
 * - Separate exit/enter triggers for precise animation control
 * - Configurable timing with exit-time and first-delay
 * - Session-aware first load handling
 * - SPA event-based integration support
 * - Loading class after 2s wait for IX3
 *
 * Usage:
 *   <div data-hs-transition="wrapper" data-hs-transition-exit-time="0.5" data-hs-transition-first-delay="0.3">
 *     <!-- Default triggers (fallback) -->
 *     <div data-hs-transition-exit-trigger>Exit animation</div>
 *     <div data-hs-transition-enter-trigger>Enter animation</div>
 *     <!-- OR -->
 *     <div data-hs-transition-trigger="exit">Exit animation</div>
 *     <div data-hs-transition-trigger="enter">Enter animation</div>
 *
 *     <!-- Namespace-specific (for Barba.js) -->
 *     <div data-hs-transition-namespace="home">
 *       <div data-hs-transition-trigger="exit">Home exit</div>
 *       <div data-hs-transition-trigger="enter">Home enter</div>
 *     </div>
 *   </div>
 */

import config from '@config';
import { getSelector, querySelector } from '@utils';
import { isWebflow, waitForIx3 } from '@utils';

const globalConfig = config._global;
const moduleConfig = config.default.transition;
const API_NAME = globalConfig.apiName;

export async function init() {
  // Check if transition wrapper exists - skip if not found
  const transitionWrapper = querySelector(moduleConfig as any, 'wrapper');
  if (!transitionWrapper) {
    return {
      result: 'transition skipped - no wrapper found',
      destroy: () => {},
    };
  }

  // Prevent double initialization (important for SPA when wrapper is outside Barba container)
  const initializedAttr = 'data-hs-transition-initialized';
  if (transitionWrapper.hasAttribute(initializedAttr)) {
    return {
      result: 'transition already initialized',
      destroy: () => {},
    };
  }

  // Mark as initialized
  transitionWrapper.setAttribute(initializedAttr, 'true');

  // Check if running in SPA mode
  const isSPA = window[API_NAME]?.settings?.isSPA || false;

  // Store event handlers and timeouts for cleanup
  const eventHandlers = new Map();
  let delayTimeout: number | null = null;
  let navTimeout: number | null = null;
  let loadingTimeout: number | null = null;

  const cleanup = () => {
    // Clear any pending timeouts
    if (delayTimeout) clearTimeout(delayTimeout);
    if (navTimeout) clearTimeout(navTimeout);
    if (loadingTimeout) clearTimeout(loadingTimeout);

    // Remove all event listeners
    eventHandlers.forEach((handler, event) => {
      if (event === 'click' || event === 'pageshow' || event === 'resize') {
        const target = event === 'click' ? document : window;
        target.removeEventListener(event, handler);
      } else {
        window.removeEventListener(event, handler);
      }
    });
    eventHandlers.clear();

    // Remove initialization flag to allow re-initialization
    transitionWrapper.removeAttribute(initializedAttr);
  };

  /**
   * Find trigger element for specific type (exit or enter) and optional namespace
   * Supports trigger patterns with priority:
   * 1. Namespace-specific triggers (if namespace provided)
   * 2. Default triggers (no namespace)
   */
  const findTrigger = (
    type: 'exit' | 'enter',
    namespace: string | null = null,
    root: Element = transitionWrapper
  ): Element | null => {
    // Get attribute names from config (extract from primary attribute)
    const getTriggerAttr =
      moduleConfig.attributes.elements.trigger.primary.match(/data-[^=]+/)?.[0];
    const getNamespaceAttr =
      moduleConfig.attributes.elements.namespace.primary.match(/data-[^=]+/)?.[0];

    if (!getTriggerAttr || !getNamespaceAttr) return null;

    // Try new syntax: data-hs-transition-trigger="exit" or "enter"
    const newSyntaxTriggers = root.querySelectorAll(`[${getTriggerAttr}="${type}"]`);

    if (newSyntaxTriggers.length > 0) {
      // If namespace specified, look inside namespace wrapper
      if (namespace) {
        const namespaceWrapper = root.querySelector(`[${getNamespaceAttr}="${namespace}"]`);
        if (namespaceWrapper) {
          const namespaceTrigger = namespaceWrapper.querySelector(`[${getTriggerAttr}="${type}"]`);
          if (namespaceTrigger) return namespaceTrigger;
        }
      }

      // Fallback to default (not inside ANY namespace wrapper)
      const defaultTrigger = Array.from(newSyntaxTriggers).find((trigger) => {
        // Check if trigger is inside any namespace wrapper
        let parent = trigger.parentElement;
        while (parent && parent !== root) {
          if (parent.hasAttribute(getNamespaceAttr)) {
            return false; // Inside a namespace, skip it
          }
          parent = parent.parentElement;
        }
        return true; // Not inside any namespace
      });

      if (defaultTrigger) return defaultTrigger;
    }

    // Try separate exit/enter trigger syntax
    const separateTriggerAttr =
      type === 'exit'
        ? moduleConfig.attributes.elements.exitTrigger.primary.match(/data-[^=]+/)?.[0]
        : moduleConfig.attributes.elements.enterTrigger.primary.match(/data-[^=]+/)?.[0];

    if (!separateTriggerAttr) return null;

    const separateTriggers = root.querySelectorAll(`[${separateTriggerAttr}]`);

    if (separateTriggers.length > 0) {
      // If namespace specified, look inside namespace wrapper
      if (namespace) {
        const namespaceWrapper = root.querySelector(`[${getNamespaceAttr}="${namespace}"]`);
        if (namespaceWrapper) {
          const namespaceTrigger = namespaceWrapper.querySelector(`[${separateTriggerAttr}]`);
          if (namespaceTrigger) return namespaceTrigger;
        }
      }

      // Fallback to default (not inside ANY namespace wrapper)
      const defaultTrigger = Array.from(separateTriggers).find((trigger) => {
        // Check if trigger is inside any namespace wrapper
        let parent = trigger.parentElement;
        while (parent && parent !== root) {
          if (parent.hasAttribute(getNamespaceAttr)) {
            return false; // Inside a namespace, skip it
          }
          parent = parent.parentElement;
        }
        return true; // Not inside any namespace
      });

      if (defaultTrigger) return defaultTrigger;
    }

    return null;
  };

  /**
   * Get exit time from wrapper element
   */
  const getExitTime = (): number => {
    const exitTimeAttr = moduleConfig.attributes.elements.exitTime.primary.match(/data-[^=]+/)?.[0];
    if (!exitTimeAttr) return 0;

    const value = transitionWrapper.getAttribute(exitTimeAttr);
    if (value) return parseFloat(value) * 1000;

    return 0;
  };

  /**
   * Get first delay from wrapper element (only applies on first load)
   */
  const getFirstDelay = (): number => {
    const firstDelayAttr =
      moduleConfig.attributes.elements.firstDelay.primary.match(/data-[^=]+/)?.[0];
    if (!firstDelayAttr) return 0;

    const value = transitionWrapper.getAttribute(firstDelayAttr);
    if (value) return parseFloat(value) * 1000;

    return 0;
  };

  /**
   * Play exit animation (when leaving page)
   */
  const playExitAnimation = (namespace: string | null = null): Promise<void> => {
    return new Promise((resolve) => {
      const exitTrigger = findTrigger('exit', namespace);
      if (!exitTrigger) {
        resolve();
        return;
      }

      const exitTime = getExitTime();

      // Trigger the animation
      (exitTrigger as HTMLElement).click();

      // Resolve after animation completes
      setTimeout(() => resolve(), exitTime);
    });
  };

  /**
   * Play enter animation (when arriving on page)
   */
  const playEnterAnimation = (namespace: string | null = null): Promise<void> => {
    return new Promise((resolve) => {
      const enterTrigger = findTrigger('enter', namespace);
      if (!enterTrigger) {
        resolve();
        return;
      }

      // Check if first load
      const isFirstLoad = !sessionStorage.getItem('transition-loaded');
      const firstDelay = getFirstDelay();

      const triggerAnimation = () => {
        (enterTrigger as HTMLElement).click();

        // Mark as loaded for session
        if (isFirstLoad) {
          sessionStorage.setItem('transition-loaded', 'true');
        }

        resolve();
      };

      // Only apply delay on first load
      if (isFirstLoad && firstDelay > 0) {
        delayTimeout = setTimeout(triggerAnimation, firstDelay) as unknown as number;
      } else {
        triggerAnimation();
      }
    });
  };

  // Always listen for custom events (works for both MPA and SPA)
  // SPA frameworks (like Barba) can dispatch these with namespace data
  const handleExitEvent = (event?: CustomEvent) => {
    const namespace = (event as any)?.detail?.namespace || null;
    playExitAnimation(namespace);
  };

  const handleEnterEvent = (event?: CustomEvent) => {
    const namespace = (event as any)?.detail?.namespace || null;
    playEnterAnimation(namespace);
  };

  eventHandlers.set('hsmain:transition-exit', handleExitEvent);
  eventHandlers.set('hsmain:transition-enter', handleEnterEvent);
  window.addEventListener('hsmain:transition-exit', handleExitEvent as EventListener);
  window.addEventListener('hsmain:transition-enter', handleEnterEvent as EventListener);

  // MPA Mode: Intercept link clicks
  if (!isSPA) {
    const linkClickHandler = (e: MouseEvent) => {
      const link = (e.target as Element).closest('a[href]') as HTMLAnchorElement;

      if (
        link &&
        link.hostname === window.location.hostname &&
        link.getAttribute('href') &&
        link.getAttribute('href')!.indexOf('#') === -1 &&
        link.getAttribute('target') !== '_blank'
      ) {
        e.preventDefault();
        const href = link.href;

        // Play exit animation, then navigate
        playExitAnimation().then(() => {
          window.location.href = href;
        });
      }
    };

    eventHandlers.set('click', linkClickHandler);
    document.addEventListener('click', linkClickHandler);

    // Handle back button navigation
    const pageShowHandler = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    eventHandlers.set('pageshow', pageShowHandler);
    window.addEventListener('pageshow', pageShowHandler);
  }

  // Hide transition on window resize (prevents visual glitches)
  const resizeHandler = () => {
    if (transitionWrapper) {
      (transitionWrapper as HTMLElement).style.display = 'none';
    }
  };

  eventHandlers.set('resize', resizeHandler);
  window.addEventListener('resize', resizeHandler);

  // On DOM ready, play enter animation (only in MPA mode)
  // In SPA mode, Barba handles all transitions via events
  if (!isSPA) {
    const domReadyHandler = async () => {
      // Wait for Webflow IX3 to be ready before triggering enter animation
      const isWf = isWebflow();
      if (isWf) {
        // Set loading class after 2s if IX3 hasn't loaded yet
        loadingTimeout = setTimeout(() => {
          (transitionWrapper as HTMLElement).classList.add(globalConfig.classes.loading);
        }, 2000) as unknown as number;

        // Wait indefinitely for IX3 (no timeout)
        await waitForIx3();

        // Clear loading timeout if IX3 loaded before 2s
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }

        // Remove loading class and add loaded class once IX3 is ready
        (transitionWrapper as HTMLElement).classList.remove(globalConfig.classes.loading);
        (transitionWrapper as HTMLElement).classList.add(globalConfig.classes.loaded);
      }

      playEnterAnimation();
    };

    eventHandlers.set(`${API_NAME}:dom-ready`, domReadyHandler);
    window.addEventListener(`${API_NAME}:dom-ready`, domReadyHandler as EventListener, {
      once: true,
    });
  }

  return {
    result: 'transition initialized',
    destroy: cleanup,
  };
}
